import { startCase } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable";
import NocOverviewModal from "./NocOverviewModal";
import {
  clearError,
  deleteNOCHigherEducationRequest,
  fetchNOCHigherEducationRequests,
  INocHigherEducationRequest,
  setCurrentRequest,
} from "../../../core/data/redux/nocHigherEducationSlice";
import DeleteModal from "../../../core/modals/deleteModal";
import type { RootState } from "../../../core/data/redux/store";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";
import SyncNocRequest from "./sync-noc-request";
import { DatePicker } from "antd";
import { ConfigProvider } from "antd";
import dayjs from "dayjs";
import RequestStatusBadge from "./RequestStatusBadge";
import DateRangeFilter from "../../../core/common/DateRangeFilter";
import RequestStatusFilter from "./RequestStatusFilter";
import { isAdminOrSubAdmin, isSupportViewer } from "../../../utils/permissionUtils";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { processNocDownloadResponse } from "../../../utils/nocDownloadUtils";

interface NOCHigherEducationProps { }

const user: any = localStorage.getItem("user");
const userJson = user && user != "undefined" ? JSON.parse(user) : {};
let userType: any = userJson?.user_type || userJson?.role;
const isManagement = isAdminOrSubAdmin(userType) || isSupportViewer(userType);

// --- Utility Functions (Standardized) ---

// ⚡ Consistent Date Format: DD-MM-YYYY
const formatDate = (dateString: string | null) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return "-";
  }
};

const getStatusBadgeClass = (status: string) => {
  if (!status) return "badge-secondary";

  switch (status.toLowerCase()) {
    case "pending":
    case "submitted":
    case "partially approved":
    case "to be approved":
      return "badge-warning";
    case "approved":
      return "badge-success";
    case "rejected":
      return "badge-danger";
    case "in_progress":
    case "under_review":
      return "badge-info";
    case "draft":
      return "badge-secondary";
    default:
      return "badge-secondary";
  }
};

// --- Component ---

const NOCHigherEducation = (props: NOCHigherEducationProps) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const routes = all_routes;

  // Redux state
  const { requests, loading, error, pagination, deleteLoading } = useSelector(
    (state: RootState) => state.nocHigherEducationState
  );

  // Local state
  const [showOverviewModal, setShowOverviewModal] = useState(false);
  const [deleteModalId, setDeleteModalId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEmpId, setFilterEmpId] = useState<string>("");
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(null);
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloadingRecordId, setDownloadingRecordId] = useState<number | null>(null);

  // Data fetching logic
  const fetchData = (
    page = pagination.page,
    limit = pagination.limit,
    query = searchQuery,
    requestStatus = selectedStatus,
    dateStart = startDate,
    dateEnd = endDate
  ) => {
    const params: any = {
      page,
      limit,
      query,
      orderColumn: "created_at",
      orderBy: "DESC",
    };
    if (requestStatus) params.requestStatus = requestStatus;
    if (dateStart) params.startDate = dateStart;
    if (dateEnd) params.endDate = dateEnd;
    dispatch(fetchNOCHigherEducationRequests(params));
  };



  // Fetch data on component mount/pagination change
  useEffect(() => {
    fetchData();
  }, [pagination.page, pagination.limit]);

  // ⚡ New: Toast for Redux errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Handle pagination change
  const handlePagination = (newPagination: any, filters: any, sorter: any) => {
    const page = newPagination.current || 1;
    const pageSize = newPagination.pageSize || pagination.limit;
    fetchData(page, pageSize, searchQuery, selectedStatus, startDate, endDate);
  };

  // Handle search action
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    fetchData(1, pagination.limit, query, selectedStatus, startDate, endDate); // ⚡ Resets to page 1 on new search
  };

  const handleApplyFilter = () => {
    fetchData(1, pagination.limit, searchQuery, selectedStatus, startDate, endDate);
  };

  const handleClearFilter = () => {
    setSelectedStatus("");
    setStartDate("");
    setEndDate("");
    fetchData(1, pagination.limit, searchQuery, "", "", "");
  };

  // Handle delete confirmation
  const handleDeleteClick = (id: number) => {
    setDeleteModalId(id);
  };

  // Handle delete confirmation response
  const handleDeleteConfirm = async (response: string) => {
    if (response === "YES" && deleteModalId) {
      try {
        await dispatch(deleteNOCHigherEducationRequest({ id: deleteModalId })).unwrap();
        toast.success("NOC request deleted successfully!");
        fetchData(pagination.page, pagination.limit, searchQuery, selectedStatus, startDate, endDate);
      } catch (error) {
        console.error("Failed to delete request:", error);
        toast.error("Failed to delete NOC request. Please try again.");
      }
    }
    setDeleteModalId(null);
  };

  // Handle view details click
  const handleViewDetailsClick = (record: INocHigherEducationRequest) => {
    dispatch(setCurrentRequest(record.id));
  };

  // Handle edit button click
  const handleEditClick = (record: INocHigherEducationRequest) => {
    dispatch(setCurrentRequest(record.id));
  };

  // Columns definition matching SAP NOC for Higher Education Previous Requests
  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 70,
      render: (_: any, __: any, index: number) => (
        <div>
          {pagination.page
            ? index + pagination.page * pagination.limit - pagination.limit + 1
            : index + 1}
        </div>
      ),
    },
    {
      title: "Reference Number",
      dataIndex: "referenceNumber",
      sorter: (a: INocHigherEducationRequest, b: INocHigherEducationRequest) =>
        (a.referenceNumber || "").localeCompare(b.referenceNumber || ""),
      render: (text: string) => (
        <span className="fw-medium font-monospace">{text || "-"}</span>
      ),
    },
    {
      title: "Request Type",
      dataIndex: "requestType",
      render: (text: string, record: any) =>
        text || record.typeOfRequest || "NOC Request",
    },
    {
      title: "Course Name",
      dataIndex: "courseName",
      sorter: (a: INocHigherEducationRequest, b: INocHigherEducationRequest) =>
        (a.courseName || "").localeCompare(b.courseName || ""),
      render: (text: string) => text || "-",
    },
    {
      title: "Institute/loc.",
      dataIndex: "institute",
      sorter: (a: INocHigherEducationRequest, b: INocHigherEducationRequest) =>
        (a.institute || "").localeCompare(b.institute || ""),
      render: (text: string, record: any) => text || record.instituteLocation || record.instituteLoc || "-",
    },
    {
      title: "Request Creation Date",
      dataIndex: "requestCreationDate",
      sorter: (a: INocHigherEducationRequest, b: INocHigherEducationRequest) =>
        new Date(a.requestCreationDate || a.created_at || 0).getTime() -
        new Date(b.requestCreationDate || b.created_at || 0).getTime(),
      render: (date: string | null, record: any) =>
        formatDate(date || record.created_at || record.requestCreationDat || record.sap_created_at),
    },
    {
      title: "Request Status",
      dataIndex: "requestStatus",
      render: (status: string, record: any) => (
        <RequestStatusBadge status={status || record.status} />
      ),
    },
    {
      title: "Last/Latest Approver",
      dataIndex: "approverName",
      sorter: (a: any, b: any) =>
        (a.approverName || a.lastApprover || "").localeCompare(b.approverName || b.lastApprover || ""),
      render: (text: string, record: any) => {
        const name = text || record.lastApprover || record.approver?.name || record.approverName;
        const id = record.approverUserID || record.approverId || record.approver?.empId;
        if (name && id) return `${name} (${formatEmpIdForDisplay(id)})`;
        if (name) return name;
        if (id) return formatEmpIdForDisplay(id);
        return "-";
      },
    },
    {
      title: "Remarks (if rejected)",
      dataIndex: "hRemarks",
      render: (text: string, record: any) => {
        const val = text || record.HRemarks || record.remarks1 || record.Remarks1 || record.remarks;
        return val || "-";
      },
    },
  ];

  // Always include Actions column so users can view/edit draft requests
  columns.push({
    title: "Actions",
    dataIndex: "actions",
    render: (text: string, record: INocHigherEducationRequest) => {
      const isDraft = Boolean(
        record.is_draft ||
        (record as any).isDraft ||
        record.requestStatus === "DRAFT" ||
        record.requestStatus === "draft" ||
        record.requestStatus === "-" ||
        !record.requestStatus
      );

      const isCompleted = String(record?.requestStatus || "").toUpperCase() === "D";

      const handleDownloadNocDoc = async (rec: any) => {
        if (downloadingRecordId === rec.id) return;
        try {
          setDownloadingRecordId(rec.id);
          const payload = {
            empId: rec.empID || rec.empId,
            type: "ZHIS",
            referenceNumber: rec.referenceNumber,
          };
          const res = await Api.post(API_URLS.NOC.HIGHER_EDUCATION_DOWNLOAD, payload);
          processNocDownloadResponse(res?.data);
        } catch (err: any) {
          toast.error(err?.message || "Failed to download NOC document.");
        } finally {
          setDownloadingRecordId(null);
        }
      };

      const isDownloading = downloadingRecordId === record.id;

      return (
        <div className="action-icon d-inline-flex align-items-center">
          <Link
            to={`${routes.noc.higherEducation}/details/${record.id}`}
            className="text-primary me-2"
            title="View Details"
            onClick={() => handleViewDetailsClick(record)}
          >
            <i className="ti ti-eye fs-16"></i>
          </Link>
          {isCompleted && (
            <button
              type="button"
              className="btn btn-link text-info p-0 me-2"
              title="Download Digital NOC"
              onClick={() => handleDownloadNocDoc(record)}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <i className="ti ti-file-text fs-16"></i>
              )}
            </button>
          )}
          {isDraft && (
            <>
              <Link
                to={`${routes.noc.higherEducation}/edit/${record.id}`}
                className="text-success me-2"
                title="Edit Request"
                onClick={() => handleEditClick(record)}
              >
                <i className="ti ti-edit fs-16"></i>
              </Link>
              <button
                type="button"
                className="btn btn-link text-danger p-0"
                title="Delete Request"
                onClick={() => handleDeleteClick(record.id)}
                disabled={deleteLoading}
                data-bs-toggle="modal"
                data-bs-target="#delete_modal"
              >
                <i className="ti ti-trash fs-16"></i>
              </button>
            </>
          )}
        </div>
      );
    },
  } as any);

  // Clear error on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">NOC for Higher Education</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Higher Education
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          {/* Card Header: Title on Left, Action Buttons on Right */}
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3 py-3">
            <h4 className="mb-0 fw-bold">NOC Applications</h4>
            <div className="d-flex align-items-center gap-2">
              {!isManagement && (
                <button
                  type="button"
                  onClick={() => setShowOverviewModal(true)}
                  className="btn btn-primary text-nowrap"
                >
                  <i className="ti ti-plus me-1"></i>
                  Create New Request
                </button>
              )}
              <SyncNocRequest nocType={'HIGHER-STUDIES'} onSyncSuccess={() => fetchData()} />
            </div>
          </div>

          <div className="card-body">
            {/* Dedicated Modern Filter Toolbar */}
            <div className="p-3 bg-light rounded-3 border mb-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <RequestStatusFilter
                    value={selectedStatus}
                    onChange={(val) => {
                      setSelectedStatus(val);
                      fetchData(1, pagination.limit, searchQuery, val, startDate, endDate);
                    }}
                  />
                  <DateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    onChangeStart={setStartDate}
                    onChangeEnd={setEndDate}
                    onApply={handleApplyFilter}
                    onClear={handleClearFilter}
                    variant="inline"
                  />
                </div>
              </div>
            </div>
            <Table
              columns={columns}
              dataSource={requests}
              Selection={false}
              paginationConfig={{
                current: pagination.page,
                pageSize: pagination.limit,
                total: pagination.total || 0,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showTotal: (total: number, range: [number, number]) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              handlePagination={handlePagination}
              loading={loading}
              handleSearch={handleSearch}
            />
          </div>
        </div>

        {/* Delete Modal */}
        <DeleteModal handleDelete={handleDeleteConfirm} />

        {/* NOC Overview Modal */}
        <NocOverviewModal
          isOpen={showOverviewModal}
          onClose={() => setShowOverviewModal(false)}
          onProceed={() => {
            setShowOverviewModal(false);
            navigate(routes.noc.higherEducationCreate);
          }}
          nocType="HIGHER_EDUCATION"
        />
      </div>
    </div>
  );
};

export default NOCHigherEducation;
